import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Space, Button, Spin, message, theme } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/PageHeader';
import { DeletePlanConfirmModal } from './components/DeletePlanConfirmModal';
import { usePlan, useDeletePlan } from './api/usePlanes';
import { useHasPermission } from '../../auth/useHasPermission';
import { EmptyState } from '../../components/EmptyState';
import dayjs from 'dayjs';
import { parseDateOnly } from '../../lib/date';
import { formatCurrency, Permission } from '@aid/shared';

export function PlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const planId = Number(id);
  const { data: plan, isLoading, error } = usePlan(planId);
  const deletePlan = useDeletePlan();
  const canUpdate = useHasPermission(Permission.PLANES_UPDATE);
  const canDelete = useHasPermission(Permission.PLANES_DELETE);
  const { token } = theme.useToken();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !plan) {
    return (
      <EmptyState
        title="Plan no encontrado"
        description="El plan que buscas no existe o fue eliminado."
      />
    );
  }

  const handleDelete = async () => {
    try {
      await deletePlan.mutateAsync(plan.id);
      message.success('Plan eliminado');
      navigate('/planes');
    } catch {
      message.error('Error al eliminar el plan');
    }
  };

  return (
    <div>
      <PageHeader
        title={plan.nombre ?? `Plan #${plan.id}`}
        subtitle={`ID: ${plan.id}`}
        actions={
          <Space wrap size={[8, 8]}>
            {canUpdate && (
              <Button icon={<EditOutlined />} onClick={() => navigate(`/planes/${plan.id}/edit`)}>
                Editar
              </Button>
            )}
            {canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteModalOpen(true)}>
                Eliminar
              </Button>
            )}
          </Space>
        }
      />
      <Card
        title="Información del plan"
        style={{
          borderRadius: 12,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadow,
        }}
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: '14px 22px',
          },
        }}
      >
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          style={{ marginTop: 8 }}
          labelStyle={{
            color: token.colorTextSecondary,
            fontWeight: 500,
            paddingTop: 14,
            paddingBottom: 14,
          }}
          contentStyle={{ paddingTop: 14, paddingBottom: 14 }}
        >
          <Descriptions.Item label="ID">{plan.id}</Descriptions.Item>
          <Descriptions.Item label="Nombre">{plan.nombre ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Precio">
            {plan.precio ? formatCurrency(plan.precio) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Comisión">
            {plan.comision ? formatCurrency(plan.comision) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Genera comisión">
            {plan.generaComision ? 'Sí' : 'No'}
          </Descriptions.Item>
          <Descriptions.Item label="Comentarios">{plan.comentarios ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Fecha de inicio">
            {/* Date-only contract: parse component-wise (legacy ISO-midnight safe). */}
            {plan.fechaInicio
              ? (parseDateOnly(plan.fechaInicio)?.format('DD MMM YYYY') ?? '-')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Fecha de fin">
            {plan.fechaFin ? (parseDateOnly(plan.fechaFin)?.format('DD MMM YYYY') ?? '-') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Creado">
            {plan.fechaCreacion ? dayjs(plan.fechaCreacion).format('DD MMM YYYY') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Modificado">
            {plan.fechaModificacion ? dayjs(plan.fechaModificacion).format('DD MMM YYYY') : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <DeletePlanConfirmModal
        open={deleteModalOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        loading={deletePlan.isPending}
      />
    </div>
  );
}
